"use client";

import { AnimatePresence, motion } from "motion/react";
import { TextLoader } from "generative-loaders";

import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";

const LIVE_UPDATE_SPRING = {
  type: "spring" as const,
  stiffness: 540,
  damping: 35,
  mass: 0.8,
};

const LIVE_UPDATE_TRANSITION = {
  layout: LIVE_UPDATE_SPRING,
  transform: LIVE_UPDATE_SPRING,
  opacity: {
    duration: 0.14,
    ease: [0.23, 1, 0.32, 1] as const,
  },
};

const LIVE_UPDATE_EXIT_TRANSITION = {
  transform: {
    duration: 0.14,
    ease: [0.23, 1, 0.32, 1] as const,
  },
  opacity: {
    duration: 0.12,
    ease: [0.23, 1, 0.32, 1] as const,
  },
};

// The mark centers on the first wrapped line: 1lh resolves against the row's
// inherited line height, so the offset tracks any typeset metric change.
const LIVE_UPDATE_MARK_CLASS =
  "mt-[calc((1lh-1.125rem)/2)] size-[1.125rem] shrink-0 self-start";

// The single progress row is visibly Twin-owned and breathes while live. The
// mark is bare, the same as the "Thinking…" line, not plated in a disc.
function TwinLeadMark(props: { active: boolean }) {
  return (
    <TwinLiveMark active={props.active} className={LIVE_UPDATE_MARK_CLASS} />
  );
}

/** One live update as the line shows it. */
export type RunLiveUpdateLineItem = {
  /** Identity for the row transition. */
  key: string;
  text: string;
  type: string;
  source?: string;
};

/**
 * The standard run's progress line: the Twin mark breathing beside the
 * newest update, which wipes in and hands over to the next. The standard
 * live-updates panel paces and rotates the updates and renders them here.
 */
export function RunLiveUpdateLine(props: {
  items: RunLiveUpdateLineItem[];
  reduceMotion?: boolean | null;
}) {
  const reduceMotion = Boolean(props.reduceMotion);
  return (
    // The newest row's TextLoader owns the polite live region; announcing the
    // whole panel as well double-reads every line to screen readers.
    // The Twin mark stays mounted outside the row transition so it never
    // re-enters, and the rows stack in one grid cell with a one-line floor, so
    // a title swapping in lands exactly where the last one sat.
    <div className="w-full max-w-[800px] px-1 py-0.5">
      <div className="typeset typeset-chat flex max-w-[800px] items-start gap-1.5 break-words [--typeset-size:0.875rem]">
        <TwinLeadMark active />
        <div className="grid min-h-[1lh] min-w-0 flex-1 overflow-hidden">
          <AnimatePresence initial={false} mode="popLayout">
            {props.items.map((item) => {
              return (
                <motion.div
                  key={item.key}
                  initial={
                    reduceMotion
                      ? { opacity: 0 }
                      : { opacity: 0, transform: "translateY(6px)" }
                  }
                  animate={{
                    opacity: 1,
                    transform: "translateY(0px)",
                  }}
                  exit={
                    reduceMotion
                      ? { opacity: 0, transition: { duration: 0.08 } }
                      : {
                          opacity: 0,
                          transform: "translateY(-6px)",
                          transition: LIVE_UPDATE_EXIT_TRANSITION,
                        }
                  }
                  transition={LIVE_UPDATE_TRANSITION}
                  className="[grid-area:1/1] min-w-0 will-change-transform"
                  data-update-type={item.type}
                >
                  <span
                    className={
                      item.source === "memory"
                        ? "font-medium text-link"
                        : undefined
                    }
                  >
                    <TextLoader
                      text={item.text}
                      variant="wipe"
                      color="currentColor"
                      className="mlx-live-update-text-loader"
                    />
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
